import { FileText, Download, Receipt, CreditCard } from 'lucide-react'
import { usePaymentHistory, formatPrice } from '../../hooks/useBilling'

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    succeeded: { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-700 dark:text-emerald-300', label: 'Payé' },
    pending: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', label: 'En attente' },
    failed: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', label: 'Échoué' },
    refunded: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300', label: 'Remboursé' },
  }
  const c = config[status] || config.pending
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  )
}

export default function AccountBilling() {
  const { payments, stats, loading, loadingMore, hasMore, loadMore } = usePaymentHistory()

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5 dark:border-zinc-700 dark:bg-zinc-800/50">
            <div className="flex items-center gap-2 text-zinc-500">
              <CreditCard className="h-4 w-4" />
              <span className="text-sm">Total dépensé</span>
            </div>
            <p className="mt-2 text-2xl font-bold text-zinc-900 dark:text-white">
              {formatPrice(stats.total_spent, stats.currency)}
            </p>
          </div>
          
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5 dark:border-zinc-700 dark:bg-zinc-800/50">
            <div className="flex items-center gap-2 text-zinc-500">
              <Receipt className="h-4 w-4" />
              <span className="text-sm">Paiements</span>
            </div>
            <p className="mt-2 text-2xl font-bold text-zinc-900 dark:text-white">
              {stats.total_payments}
            </p>
          </div>
          
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5 dark:border-zinc-700 dark:bg-zinc-800/50">
            <div className="flex items-center gap-2 text-zinc-500">
              <FileText className="h-4 w-4" />
              <span className="text-sm">Dernier paiement</span>
            </div>
            <p className="mt-2 text-lg font-bold text-zinc-900 dark:text-white">
              {stats.last_payment_at 
                ? new Date(stats.last_payment_at).toLocaleDateString('fr-FR')
                : '-'}
            </p>
          </div>
        </div>
      )}

      {/* Payments Table */}
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50">
        <div className="border-b border-zinc-200 p-5 dark:border-zinc-700">
          <h3 className="font-semibold text-zinc-900 dark:text-white">Historique des paiements</h3>
        </div>
        
        {payments.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800">
              <Receipt className="h-6 w-6 text-zinc-400" />
            </div>
            <p className="text-zinc-500">Aucun paiement pour le moment</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200 text-left text-xs font-medium uppercase tracking-wider text-zinc-500 dark:border-zinc-700">
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Description</th>
                    <th className="px-5 py-3">Montant</th>
                    <th className="px-5 py-3">Statut</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                  {payments.map((payment) => (
                    <tr key={payment.public_id} className="hover:bg-zinc-100/50 dark:hover:bg-zinc-700/30">
                      <td className="whitespace-nowrap px-5 py-4 text-sm text-zinc-900 dark:text-white">
                        {payment.created_at 
                          ? new Date(payment.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })
                          : '-'}
                      </td>
                      <td className="px-5 py-4 text-sm text-zinc-600 dark:text-zinc-400">
                        {payment.description || 'Abonnement'}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm font-medium text-zinc-900 dark:text-white">
                        {formatPrice(payment.amount, payment.currency)}
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge status={payment.status} />
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {payment.invoice_pdf_url && (
                            <a
                              href={payment.invoice_pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rounded-lg p-2 text-zinc-500 transition hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-zinc-600 dark:hover:text-zinc-200"
                              title="Voir la facture"
                            >
                              <FileText className="h-4 w-4" />
                            </a>
                          )}
                          {payment.receipt_url && (
                            <a
                              href={payment.receipt_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rounded-lg p-2 text-zinc-500 transition hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-zinc-600 dark:hover:text-zinc-200"
                              title="Télécharger le reçu"
                            >
                              <Download className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {hasMore && (
              <div className="border-t border-zinc-200 p-4 text-center dark:border-zinc-700">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="rounded-full bg-zinc-100 px-5 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-200 disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                >
                  {loadingMore ? 'Chargement...' : 'Charger plus'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
